import { useEffect, useState } from "react";
import { Modal } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import careService from "../../services/careService";
import { useFileUpload } from "../../hooks/useFileUpload";

const extractionFieldsFrom = (document = {}) =>
  (Array.isArray(document?.sections) ? document.sections : []).flatMap((section) =>
    (section?.subsections || []).flatMap((subsection) =>
      (subsection?.fields || []).map((field) => ({
        ...field,
        sectionKey: section.sectionKey || "",
      }))),
  );

const extractionText = (value) => (value === undefined || value === null ? "" : String(value).trim());

const extractedValue = (fields, patterns) => {
  const match = fields.find((field) => patterns.some((pattern) => pattern.test(extractionText(field.label))));
  return extractionText(match?.value);
};

const normalizeDate = (value) => {
  const text = extractionText(value);
  if (!text) return "";
  const isoMatch = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2].padStart(2, "0")}-${isoMatch[3].padStart(2, "0")}`;
  const usMatch = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (usMatch) {
    const year = usMatch[3].length === 2 ? `19${usMatch[3]}` : usMatch[3];
    return `${year}-${usMatch[1].padStart(2, "0")}-${usMatch[2].padStart(2, "0")}`;
  }
  const namedMonth = text.match(/\b(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})\b/) ||
    text.match(/\b([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})\b/);
  if (!namedMonth) return "";
  const months = {
    january: "01", february: "02", march: "03", april: "04", may: "05", june: "06",
    july: "07", august: "08", september: "09", october: "10", november: "11", december: "12",
  };
  if (/^\d/.test(namedMonth[1])) {
    const [, day, monthName, year] = namedMonth;
    return months[monthName.toLowerCase()] ? `${year}-${months[monthName.toLowerCase()]}-${day.padStart(2, "0")}` : "";
  }
  const [, monthName, day, year] = namedMonth;
  return months[monthName.toLowerCase()] ? `${year}-${months[monthName.toLowerCase()]}-${day.padStart(2, "0")}` : "";
};

const normalizeSex = (value) => {
  const text = extractionText(value).toLowerCase();
  if (/female|woman|girl/.test(text)) return "FEMALE";
  if (/male|man|boy/.test(text)) return "MALE";
  if (/other|non.?binary/.test(text)) return "OTHER";
  return "";
};

const residentFormFromExtraction = (document) => {
  const fields = extractionFieldsFrom(document);
  const fullName = extractedValue(fields, [/^(client|resident)\s+name$/i, /^(client|resident)\s+full\s+name$/i, /^full\s+name$/i]);
  const nameParts = fullName.split(/\s+/).filter(Boolean);
  const profileDateField = fields.find((field) => /date\s+of\s+birth/i.test(extractionText(field.value)));
  const profileDate = profileDateField?.value
    ? normalizeDate(extractionText(profileDateField.value).match(/date\s+of\s+birth\s*[:|-]?\s*([^|\n]+)/i)?.[1] || "")
    : "";
  return {
    firstName: extractedValue(fields, [/^(client|resident)\s+first\s+name$/i, /^first\s+name$/i]) || nameParts[0] || "",
    middleName: extractedValue(fields, [/^(client|resident)\s+middle\s+name$/i, /^middle\s+name$/i]) || (nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""),
    lastName: extractedValue(fields, [/^(client|resident)\s+last\s+name$/i, /^last\s+name$/i]) || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""),
    preferredName: extractedValue(fields, [/preferred\s+name/i, /nickname/i]),
    dateOfBirth: normalizeDate(extractedValue(fields, [/date\s+of\s+birth/i, /^dob$/i, /birth\s+date/i])) || profileDate,
    sex: normalizeSex(extractedValue(fields, [/^(client|resident)\s+(sex|gender)$/i, /^(sex|gender)$/i])),
    primaryLanguage: extractedValue(fields, [/preferred\s+language/i, /primary\s+language/i, /^language$/i]),
  };
};

const emptyForm = {
  firstName: "",
  middleName: "",
  lastName: "",
  preferredName: "",
  dateOfBirth: "",
  sex: "",
  primaryLanguage: "",
  processStartDate: new Date().toISOString().slice(0, 10),
};

const selectedWorkspaceIdFromStorage = () => {
  try {
    const direct = localStorage.getItem("selectedWorkspaceId");
    if (direct) return direct;
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith("selectedWorkspaceId:")) {
        const value = localStorage.getItem(key);
        if (value) return value;
      }
    }
  } catch {
    return "";
  }
  return "";
};

const sexOptions = [
  ["", "Select sex"],
  ["FEMALE", "Female"],
  ["MALE", "Male"],
  ["OTHER", "Other"],
];

const languageOptions = [
  "English",
  "Spanish",
  "Chinese",
  "Tagalog",
  "Vietnamese",
  "Arabic",
  "French",
  "Korean",
  "Russian",
  "German",
  "Hindi",
  "Portuguese",
  "Japanese",
  "Farsi",
  "Amharic",
  "Somali",
  "Swahili",
  "American Sign Language",
];

const identityFields = [
  ["firstName", "First name", "text", true],
  ["middleName", "Middle name", "text"],
  ["lastName", "Last name", "text", true],
  ["preferredName", "Preferred name", "text"],
  ["dateOfBirth", "Date of birth", "date"],
];

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const extractionWaitMessages = [
  "Extraction is still running… please wait a few seconds.",
  "Reading the assessment pages and matching resident details…",
  "Almost finished—reviewing the extracted fields…",
  "Thanks for waiting—finalizing the extraction…",
];

const createdAssessmentProcessId = (created) =>
  created?.id ||
  created?.assessmentProcessId ||
  created?.processId ||
  created?.assessmentProcess?.id ||
  "";

const createdResidentId = (created) =>
  created?.residentId ||
  created?.id ||
  created?.resident?.id ||
  created?.resident?.residentId ||
  created?.data?.residentId ||
  created?.data?.id ||
  "";

export default function NewResidentAssessmentPage() {
  const navigate = useNavigate();
  const { selectedWorkspaceId } = useWorkspace();
  const { uploadFile } = useFileUpload();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [primaryLanguageOther, setPrimaryLanguageOther] = useState(false);
  const [assessmentFile, setAssessmentFile] = useState(null);
  const [uploadedAssessmentDocument, setUploadedAssessmentDocument] = useState(null);
  const [importingAssessment, setImportingAssessment] = useState(false);
  const [assessmentOperation, setAssessmentOperation] = useState("");
  const [importMessage, setImportMessage] = useState("");
  const [importProgress, setImportProgress] = useState({ value: 0, label: "" });
  const [, setImportStatusIndex] = useState(0);
  const [importedAssessmentExtraction, setImportedAssessmentExtraction] = useState(null);

  useEffect(() => {
    if (!importingAssessment || assessmentOperation !== "extracting") return undefined;
    const progressTimer = window.setInterval(() => {
      setImportProgress((current) => {
        if (current.value >= 90) return current;
        const value = Math.min(90, current.value + (current.value < 35 ? 8 : 4));
        return {
          value,
          label: value >= 90 ? extractionWaitMessages[0] : "Extracting resident details…",
        };
      });
    }, 700);
    return () => window.clearInterval(progressTimer);
  }, [assessmentOperation, importingAssessment]);

  useEffect(() => {
    if (!importingAssessment || assessmentOperation !== "extracting") return undefined;
    const statusTimer = window.setInterval(() => {
      setImportStatusIndex((current) => {
        const next = (current + 1) % extractionWaitMessages.length;
        setImportProgress((progress) => progress.value >= 90
          ? { ...progress, label: extractionWaitMessages[next] }
          : progress);
        return next;
      });
    }, 1800);
    return () => window.clearInterval(statusTimer);
  }, [assessmentOperation, importingAssessment]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };
  const isPrimaryLanguageOther = primaryLanguageOther || Boolean(form.primaryLanguage && !languageOptions.includes(form.primaryLanguage));
  const selectedLanguageValue = isPrimaryLanguageOther
    ? "Other"
    : languageOptions.includes(form.primaryLanguage)
    ? form.primaryLanguage
    : "";

  const uploadAssessment = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAssessmentFile(file);
    setUploadedAssessmentDocument(null);
    setImportedAssessmentExtraction(null);
    setImportingAssessment(true);
    setAssessmentOperation("uploading");
    setImportStatusIndex(0);
    setImportProgress({ value: 20, label: "Uploading document…" });
    setImportMessage("");
    setError("");
    try {
      const upload = await uploadFile(null, file, {
        confirmUpload: true,
        fileType: file.type || "DOCUMENT",
        includeUserId: false,
        preserveOriginal: true,
        purpose: "OTHER",
        skipUserValidation: true,
      });
      const fileId = upload?.fileId || upload?.file?.id || null;
      if (!fileId) throw new Error("The document was uploaded, but no file ID was returned.");
      setUploadedAssessmentDocument({
        fileId,
        storageFileId: fileId,
        fileName: file.name,
        originalFileName: file.name,
        pageNumber: 1,
        pageTitle: "Assessment document",
        description: "Uploaded state assessment document",
        contentType: file.type || "",
        type: file.type || "",
        size: file.size,
        confirmed: Boolean(upload.confirmed),
        storageStatus: upload.confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED",
        uploaded: true,
        uploadedAt: new Date().toISOString(),
      });
      setImportProgress({ value: 100, label: "Upload complete" });
      setImportMessage("Document uploaded. You can continue without extraction or extract resident details now.");
    } catch (err) {
      setAssessmentFile(null);
      setUploadedAssessmentDocument(null);
      setImportProgress({ value: 0, label: "" });
      setError(err?.response?.data?.message || err?.message || "Unable to upload the assessment document.");
    } finally {
      setImportingAssessment(false);
      setAssessmentOperation("");
      event.target.value = "";
    }
  };

  const extractAssessment = async () => {
    if (!assessmentFile || !uploadedAssessmentDocument || importingAssessment) return;
    setImportingAssessment(true);
    setAssessmentOperation("extracting");
    setImportStatusIndex(0);
    setImportProgress({ value: 8, label: "Extracting resident details…" });
    setImportMessage("");
    setError("");
    try {
      const extraction = await careService.residents.importOnboardingAssessment([assessmentFile]);
      setImportProgress({ value: 100, label: "Extraction complete" });
      setImportedAssessmentExtraction(extraction);
      const imported = residentFormFromExtraction(extraction);
      const importedCount = Object.values(imported).filter(Boolean).length;
      setForm((current) => ({ ...current, ...imported }));
      setPrimaryLanguageOther(Boolean(imported.primaryLanguage && !languageOptions.includes(imported.primaryLanguage)));
      setImportMessage(importedCount
        ? `Extraction complete. ${importedCount} resident fields were filled automatically. Review them before continuing.`
        : "Extraction complete, but no resident identity fields were found. Enter the details manually.");
    } catch (err) {
      setImportProgress({ value: 100, label: "Upload complete" });
      setError(err?.response?.data?.message || err?.message || "Unable to extract the uploaded assessment.");
    } finally {
      setImportingAssessment(false);
      setAssessmentOperation("");
    }
  };

  const confirmAssessmentExtraction = () => {
    if (!assessmentFile || !uploadedAssessmentDocument || importingAssessment) return;
    Modal.confirm({
      title: "Confirm State Initial Assessment",
      content: (
        <div>
          <p>Confirm that <strong className="state-initial-confirm-file">{assessmentFile.name}</strong> is the resident's State Initial Assessment document.</p>
          <p>Only continue if this is the correct document. Extraction will use its contents to fill resident details.</p>
        </div>
      ),
      okText: "Confirm and extract",
      cancelText: "Cancel",
      className: "state-initial-confirm-modal",
      okButtonProps: { className: "state-initial-confirm-primary" },
      cancelButtonProps: { className: "state-initial-confirm-cancel" },
      onOk: extractAssessment,
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    const today = todayInputValue();
    if (form.dateOfBirth && form.dateOfBirth > today) {
      setError("Date of birth cannot be in the future.");
      setSaving(false);
      return;
    }
    const workspaceId = selectedWorkspaceId || selectedWorkspaceIdFromStorage();
    if (!workspaceId) {
      setError("Select a workspace before creating a resident assessment.");
      setSaving(false);
      return;
    }
    const residentName = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(" ");
    const personalInformation = {
      firstName: form.firstName.trim(),
      middleName: form.middleName.trim(),
      lastName: form.lastName.trim(),
      preferredName: form.preferredName.trim(),
      birthDate: form.dateOfBirth || null,
      dateOfBirth: form.dateOfBirth || null,
      dob: form.dateOfBirth || null,
      sex: form.sex || null,
      gender: form.sex || null,
      primaryLanguage: form.primaryLanguage.trim(),
      language: form.primaryLanguage.trim(),
      preferredLanguage: form.primaryLanguage.trim(),
    };
    const residentPayload = {
      workspaceId,
      status: "PROSPECTIVE",
      residentName,
      fullName: residentName,
      firstName: personalInformation.firstName,
      middleName: personalInformation.middleName,
      lastName: personalInformation.lastName,
      preferredName: personalInformation.preferredName,
      birthDate: personalInformation.birthDate,
      dateOfBirth: personalInformation.dateOfBirth,
      dob: personalInformation.dob,
      sex: personalInformation.sex,
      gender: personalInformation.gender,
      primaryLanguage: personalInformation.primaryLanguage,
      language: personalInformation.language,
      preferredLanguage: personalInformation.preferredLanguage,
      personalInformation,
    };
    const assessmentBasePayload = {
      workspaceId,
      assessmentType: "NEW_RESIDENT_ASSESSMENT",
      patientType: "NEW",
      residentName,
      name: residentName ? `New Resident Care Process - ${residentName}` : "New Resident Care Process",
      processStartDate: form.processStartDate || null,
      status: "OPEN",
      stage: "STATE_INITIAL_ASSESSMENT",
      workflowStatus: "OPEN",
      currentScreen: "state-initial-assessment",
    };
    const initialStateAssessment = {
      status: "DRAFT",
      stage: "STATE_INITIAL_ASSESSMENT",
      assessmentDate: form.processStartDate || null,
      bioData: {
        ...personalInformation,
        personalInformation,
      },
      assessmentExtraction: importedAssessmentExtraction || null,
    };

    try {
      const resident = await careService.residents.create(residentPayload);
      const residentId = createdResidentId(resident);
      if (!residentId) throw new Error("Resident was created, but the API did not return a resident id.");

      const processPayload = {
        ...assessmentBasePayload,
        initialStateAssessment: JSON.stringify(initialStateAssessment),
        assessmentExtraction: importedAssessmentExtraction || null,
        residentId,
        resident: {
          ...residentPayload,
          id: residentId,
          residentId,
        },
      };
      const createdProcess = await careService.assessmentProcesses.create(processPayload);
      const processId = createdAssessmentProcessId(createdProcess);
      let persistedInitialStateAssessment = initialStateAssessment;
      if (processId) {
        try {
          const uploadedDocuments = uploadedAssessmentDocument ? [uploadedAssessmentDocument] : [];
          persistedInitialStateAssessment = {
            ...initialStateAssessment,
            pageCount: uploadedDocuments.length,
            uploadedDocuments,
          };
          if (importedAssessmentExtraction || uploadedDocuments.length) {
            await careService.assessmentRecords.saveForProcess(
              processId,
              "initial-assessment",
              { payload: persistedInitialStateAssessment },
            );
          }
        } catch {
          // The process remains usable if storage persistence is unavailable.
        }
      }
      const processWithResident = {
        ...createdProcess,
        residentId,
        residentName,
        residentFirstName: personalInformation.firstName,
        residentMiddleName: personalInformation.middleName,
        residentLastName: personalInformation.lastName,
        residentPreferredName: personalInformation.preferredName,
        residentDateOfBirth: personalInformation.birthDate,
        residentBirthDate: personalInformation.birthDate,
        residentSex: personalInformation.sex,
        residentGender: personalInformation.gender,
        residentPrimaryLanguage: personalInformation.primaryLanguage,
        residentLanguage: personalInformation.language,
        initialStateAssessment: persistedInitialStateAssessment,
        assessmentExtraction: importedAssessmentExtraction || null,
        stateAssessment: persistedInitialStateAssessment,
        resident: {
          ...residentPayload,
          id: residentId,
          residentId,
        },
      };
      const nextId = processId || createdProcess?.stateAssessmentId;
      navigate(nextId ? `/assessments/${nextId}/new` : "/assessments", {
        state: {
          justCreatedAssessment: true,
          assessmentResourceType: "assessmentProcess",
          assessment: processWithResident,
        },
      });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Unable to create the prospective resident assessment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CareShell className="dashboard-content residents-page assessments-page new-resident-page">
      <div className="new-resident-hero">
        <div>
          <div className="crumbs">Resident Care Processes <i className="ti ti-chevron-right" /> New Resident</div>
          <h1>New Resident Care Process</h1>
          <p>Capture the minimum resident information needed to create a prospective resident and start the resident care process.</p>
        </div>
        <div className="new-resident-actions">
          <Link className="btn new-assessment-blue-action" to="/assessments"><i className="ti ti-arrow-left" /> Care Processes</Link>
        </div>
      </div>

      <Panel title="Prospective Resident">
        {error ? <p className="form-error">{error}</p> : null}
        <section className="assessment-import-card new-assessment-import-card">
          <div>
            <h3><i className="ti ti-file-upload" /> Upload state assessment</h3>
            <p>Upload the document first. Extraction is optional and only starts when you select Extract details.</p>
          </div>
          <label className={`assessment-upload ${importingAssessment ? "is-loading" : ""}`}>
            <i className={`ti ${importingAssessment ? "ti-loader-2" : "ti-file-upload"}`} />
            <strong>{importingAssessment
              ? assessmentOperation === "extracting" ? "Extracting resident details…" : "Uploading document…"
              : assessmentFile ? assessmentFile.name : "Choose assessment file"}</strong>
            <span>PDF, DOC, DOCX, or image</span>
            <input
              className="assessment-file-input"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,image/heic,image/heif"
              onChange={uploadAssessment}
              disabled={importingAssessment}
            />
          </label>
          {importingAssessment || importProgress.value === 100 ? (
            <div className="new-assessment-import-progress" aria-live="polite">
              <div className="new-assessment-import-progress-label">
                <span><i className={`ti ${importingAssessment ? "ti-loader-2 new-assessment-import-spinner" : "ti-circle-check"}`} aria-hidden="true" /> {importProgress.label}</span>
                <strong>{importProgress.value}%</strong>
              </div>
              <div className="new-assessment-import-progress-track">
                <span style={{ width: `${importProgress.value}%` }} />
              </div>
            </div>
          ) : null}
          {uploadedAssessmentDocument ? (
            <div className="assessment-import-actions">
              <button
                className="mini-btn success"
                type="button"
                onClick={confirmAssessmentExtraction}
                disabled={importingAssessment || Boolean(importedAssessmentExtraction)}
              >
                <i className="ti ti-sparkles" />
                {importedAssessmentExtraction ? "Details extracted" : "Extract details"}
              </button>
              <span>Optional. You can continue without extracting.</span>
            </div>
          ) : null}
          {importMessage ? <div className="resident-submit-success"><i className="ti ti-circle-check" /><span>{importMessage}</span></div> : null}
        </section>
        <form className="new-assessment-minimal-form" onSubmit={submit}>
          <section className="state-assessment-bio-card tone-green">
            <h4>Resident Identity</h4>
            <div className="resident-form-grid compact">
              {identityFields.map(([name, label, type, required]) => (
                <label className="resident-form-field" key={name}>
                  <span>{label}</span>
                  <input
                    max={name === "dateOfBirth" ? todayInputValue() : undefined}
                    required={Boolean(required)}
                    type={type}
                    value={form[name]}
                    onChange={(event) => updateField(name, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="state-assessment-bio-card tone-blue">
            <h4>Demographics</h4>
            <div className="resident-form-grid compact">
              <label className="resident-form-field">
                <span>Sex</span>
                <select value={form.sex} onChange={(event) => updateField("sex", event.target.value)}>
                  {sexOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                </select>
              </label>
              <label className="resident-form-field">
                <span>Primary language</span>
                <select
                  value={selectedLanguageValue}
                  onChange={(event) => {
                    const value = event.target.value;
                    setPrimaryLanguageOther(value === "Other");
                    updateField("primaryLanguage", value === "Other" ? "" : value);
                  }}
                >
                  <option value="">Select language</option>
                  {languageOptions.map((language) => (
                    <option value={language} key={language}>{language}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </label>
              {isPrimaryLanguageOther ? (
                <label className="resident-form-field">
                  <span>Specify language</span>
                  <input
                    type="text"
                    value={form.primaryLanguage}
                    onChange={(event) => updateField("primaryLanguage", event.target.value)}
                  />
                </label>
              ) : null}
            </div>
          </section>

          <section className="state-assessment-bio-card tone-yellow">
            <h4>Assessment Setup</h4>
            <div className="resident-form-grid compact">
              <label className="resident-form-field">
                <span>Process start date</span>
                <input
                  type="date"
                  value={form.processStartDate}
                  onChange={(event) => updateField("processStartDate", event.target.value)}
                />
              </label>
            </div>
          </section>
          <div className="assessment-drawer-actions new-assessment-minimal-actions">
            <Link className="btn new-assessment-blue-action" to="/assessments">Cancel</Link>
            <button className="btn demo-cta" type="submit" disabled={saving || importingAssessment}>
              {saving ? <><i className="ti ti-loader-2" /> Saving...</> : <><i className="ti ti-user-plus" /> Create Resident Care Process</>}
            </button>
          </div>
        </form>
      </Panel>
    </CareShell>
  );
}
